import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, EmptyState, PageHeader, Spinner, StatusPill } from '../components/ui';
import { ClassesAPI } from '../services/endpoints/admin';
import { MediaAPI } from '../services/endpoints/media';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { ClassEpisode, Collection } from '../types/api';
import { formatDate, formatDuration } from '../utils/format';
import './common.css';
import './MediaDetailPage.css';

export function ClassDetailPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [cls, setCls] = useState<Collection | null>(null);
  const [episodes, setEpisodes] = useState<ClassEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const { run, busy } = useDispatchAction();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, eps] = await Promise.all([ClassesAPI.get(id), ClassesAPI.episodes(id)]);
      setCls(c ?? null);
      setEpisodes(Array.isArray(eps?.episodes) ? eps.episodes : []);
    } catch {
      setCls(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggleFeatured = async () => {
    if (!cls) return;
    const next = !cls.featured;
    const updated = await run(next ? 'Feature class' : 'Unfeature class', () => ClassesAPI.setFeatured(id, next));
    if (updated) setCls((c) => (c ? { ...c, featured: next } : c));
  };

  const toggleDisabled = async () => {
    if (!cls) return;
    const next = !cls.deleted;
    const updated = await run(next ? 'Disable class' : 'Enable class', () => ClassesAPI.setDisabled(id, next));
    if (updated) setCls((c) => (c ? { ...c, deleted: next } : c));
  };

  const toggleRecorded = async (ep: ClassEpisode) => {
    const mid = ep.media?.id;
    if (!mid) return;
    const isPublished = ep.media?.status === 'PUBLISHED';
    const ok = await run(isPublished ? 'Unpublish episode' : 'Publish episode',
      () => (isPublished ? MediaAPI.unpublish(mid) : MediaAPI.publish(mid)));
    if (ok !== null) {
      setEpisodes((prev) => prev.map((e) =>
        e.media?.id === mid ? { ...e, media: { ...e.media!, status: isPublished ? 'UNPUBLISHED' : 'PUBLISHED' } } : e));
    }
  };

  const toggleLive = async (ep: ClassEpisode) => {
    const lid = ep.liveClass?.id;
    if (!lid) return;
    const next = !ep.liveClass?.deleted;
    const ok = await run(next ? 'Disable live episode' : 'Enable live episode',
      () => ClassesAPI.setLiveDisabled(lid, next));
    if (ok !== null) {
      setEpisodes((prev) => prev.map((e) =>
        e.liveClass?.id === lid ? { ...e, liveClass: { ...e.liveClass!, deleted: next } } : e));
    }
  };

  if (loading) return <Card><Spinner /> Loading…</Card>;
  if (!cls) return <Card><EmptyState title="Class not found" action={<Button onClick={() => nav('/classes')}>← Back to classes</Button>} /></Card>;

  return (
    <div>
      <PageHeader
        eyebrow={`04 — CLASS · ${id.slice(0, 8)}`}
        title={<>{cls.title || cls.name || <em>(untitled)</em>}</>}
        lede={<>
          {cls.deleted ? <StatusPill tone="rust">Disabled</StatusPill> : <StatusPill tone="moss">Active</StatusPill>}
          {cls.featured && <> <StatusPill tone="brass">Featured</StatusPill></>}
          {' '}{cls.type === 'AUDIO' ? 'Audio' : 'Video'} class · {cls.count ?? 0} episode{(cls.count ?? 0) !== 1 ? 's' : ''}
          {cls.creatorName ? ` · by ${cls.creatorName}` : ''}
        </>}
        actions={<>
          <Button onClick={() => nav('/classes')}>← Back</Button>
          <Button tone={cls.featured ? 'ghost' : 'ember'} disabled={busy} onClick={toggleFeatured}>
            {cls.featured ? 'Unfeature' : 'Feature'}
          </Button>
          <Button tone={cls.deleted ? 'ember' : 'danger'} disabled={busy} onClick={toggleDisabled}>
            {cls.deleted ? 'Enable class' : 'Disable class'}
          </Button>
        </>}
      />

      <div className="md-grid">
        <div className="md-col">
          <Card padded={false}>
            <div style={{ padding: '14px 18px 0' }}><h2 className="md-h">Episodes</h2></div>
            {episodes.length === 0 ? (
              <div style={{ padding: 18 }}><EmptyState title="No episodes yet" hint="Recorded uploads and live sessions in this class will appear here." /></div>
            ) : (
              <table className="nb-table">
                <thead>
                  <tr><th>#</th><th>Episode</th><th>Kind</th><th>Status</th><th>Added</th><th /></tr>
                </thead>
                <tbody>
                  {episodes.map((ep, i) => {
                    const recorded = ep.kind === 'RECORDED';
                    const title = (recorded ? ep.media?.title : ep.liveClass?.title) || `Episode ${i + 1}`;
                    return (
                      <tr key={(recorded ? ep.media?.id : ep.liveClass?.id) ?? i}>
                        <td className="nb-table__mono">{String(i + 1).padStart(2, '0')}</td>
                        <td>
                          <div className="nb-table__primary">{title}</div>
                          {recorded && ep.media?.duration != null && (
                            <div className="nb-table__sub">{formatDuration(ep.media.duration)}</div>
                          )}
                          {!recorded && ep.liveClass?.scheduledStartTime && (
                            <div className="nb-table__sub">{formatDate(ep.liveClass.scheduledStartTime)}</div>
                          )}
                        </td>
                        <td><StatusPill tone={recorded ? 'brass' : 'ember'}>{recorded ? 'Recorded' : 'Live'}</StatusPill></td>
                        <td><EpisodeStatus ep={ep} /></td>
                        <td className="nb-table__mono">{formatDate(ep.createdTimestamp)}</td>
                        <td>
                          {recorded ? (
                            <Button size="sm" tone={ep.media?.status === 'PUBLISHED' ? 'ghost' : 'ember'} disabled={busy} onClick={() => toggleRecorded(ep)}>
                              {ep.media?.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                            </Button>
                          ) : (
                            <Button size="sm" tone={ep.liveClass?.deleted ? 'ember' : 'ghost'} disabled={busy} onClick={() => toggleLive(ep)}>
                              {ep.liveClass?.deleted ? 'Enable' : 'Disable'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <aside className="md-rail">
          <Card>
            <h3 className="md-h-mono">Class</h3>
            <dl className="md-static">
              <div className="md-row"><dt>ID</dt><dd className="nb-table__mono">{cls.id}</dd></div>
              <div className="md-row"><dt>Name</dt><dd className="nb-table__mono">{cls.name}</dd></div>
              <div className="md-row"><dt>Access</dt><dd>{cls.accessType || 'FREE'}</dd></div>
              <div className="md-row"><dt>Created</dt><dd className="nb-table__mono">{formatDate(cls.createdTimestamp)}</dd></div>
            </dl>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function EpisodeStatus({ ep }: { ep: ClassEpisode }) {
  if (ep.kind === 'RECORDED') {
    const s = ep.media?.status;
    if (s === 'PUBLISHED')   return <StatusPill tone="moss">Published</StatusPill>;
    if (s === 'UNPUBLISHED') return <StatusPill tone="ember">Unpublished</StatusPill>;
    if (s === 'FILE_UPLOADED') return <StatusPill tone="brass">Ready</StatusPill>;
    return <StatusPill tone="brass">Draft</StatusPill>;
  }
  if (ep.liveClass?.deleted) return <StatusPill tone="rust">Disabled</StatusPill>;
  const s = ep.liveClass?.status;
  if (s === 'LIVE')  return <StatusPill tone="moss">Live</StatusPill>;
  if (s === 'ENDED') return <StatusPill tone="ember">Ended</StatusPill>;
  return <StatusPill tone="brass">Scheduled</StatusPill>;
}
